'use client';

import {
  createContext, ReactNode, useCallback, useContext, useMemo, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { startResumeInterviewSession, type ResumeInterviewKind, type VoiceSession } from '@services/voice/voiceSessionApi';

interface ResumeInterviewChatStateValue {
  /** Resume-interview chat stream — runs over the bestie chat stream on the backend thread_id. */
  stream: ReturnType<typeof useStreamStateV2>['stream'];
  /** True once the backend session has been fetched and the chat is ready to accept messages. */
  isReady: boolean;
  /** True while the backend session is being fetched. */
  isStarting: boolean;
  /** Fetch the backend resume-interview session (thread_id + prompt + extraction_schema). */
  startSession: (questions: string[]) => Promise<void>;
  /** Submit a chat message. The first message also carries the backend prompt + extraction_schema. */
  submitChat: (text: string) => Promise<void>;
}

const ResumeInterviewChatContext = createContext<ResumeInterviewChatStateValue | undefined>(undefined);

/**
 * @description Runs a RESUME-INTERVIEW chat over the existing bestie chat stream, but driven by a
 * backend-built session: the backend returns a `thread_id`, a `prompt` and an `extraction_schema`.
 * The chat MUST run on that backend `thread_id` (NOT the anon session id) so that answers persist
 * against the backend `conversation_session` and route back to `saveResumeInterview`.
 *
 * This is intentionally a SEPARATE context from `BestieChatStateContext` so the generic Bestie chat
 * stays exactly as-is (no thread_id injection, no prompt/extraction_schema).
 */
export const ResumeInterviewChatProvider = ({ children, locale, kind = 'profile' }: { children: ReactNode; locale: Locale; kind?: ResumeInterviewKind }) => {
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  // Inject the backend thread_id so the chat thread matches the backend conversation_session.
  // The backend created this thread via chat/invoke with the prompt + variables +
  // extraction_schema and returned the agent's opening line; we seed it directly into the
  // stream below. chat/stream then just continues the conversation — no prompt/schema resend.
  const { stream, startStream, seedMessage } = useStreamStateV2(null, { threadId: session?.threadId });

  const startSession = useCallback(async (questions: string[]) => {
    setIsStarting(true);
    try {
      const s = await startResumeInterviewSession(questions, 'chat', locale, undefined, kind);
      setSession(s);
      // Show the agent's opening line (returned by chat/invoke) as the first message.
      if (s.firstMessage) {
        seedMessage({ id: `resume-interview-opening-${s.threadId}`, type: 'ai', content: s.firstMessage });
      }
    } finally {
      setIsStarting(false);
    }
  }, [locale, kind, seedMessage]);

  const submitChat = useCallback(async (text: string) => {
    if (!session) {
      throw new Error('ResumeInterviewChat: session not started — call startSession() first');
    }

    const message = { id: uuidv4(), content: text, type: 'human' as const };

    await startStream(message, '', {
      configurations: { user_locale: locale || 'ro' },
    });
  }, [session, startStream, locale]);

  const value = useMemo<ResumeInterviewChatStateValue>(
    () => ({
      stream,
      isReady: Boolean(session),
      isStarting,
      startSession,
      submitChat,
    }),
    [stream, session, isStarting, startSession, submitChat],
  );

  return <ResumeInterviewChatContext.Provider value={value}>{children}</ResumeInterviewChatContext.Provider>;
};

/** Access the resume-interview chat state. Returns undefined outside the provider. */
export const useResumeInterviewChatStateOptional = () => useContext(ResumeInterviewChatContext);

export const useResumeInterviewChatState = () => {
  const ctx = useContext(ResumeInterviewChatContext);
  if (!ctx) throw new Error('useResumeInterviewChatState must be used within ResumeInterviewChatProvider');
  return ctx;
};
